import type {TFunction} from "i18next";
import {UserType} from "crqlar-react-common/dist/models/identity/user.model";

import {IdFilterKeys, StoredIdFilter, readIdFilter, writeIdFilter} from "./reservationTriggers";

/**
 * TEAM_MEMBERS contract, mirrored by the API:
 * - `userTypes`, `appIds`, `userIds` are explicit opt-in: matching any filled list reaches a member; all empty reaches nobody.
 * - `excludedUserIds` removes people a type or app brings in; someone named in `userIds` is always in.
 * - `*Names` are index-aligned display snapshots. Only active members are ever reached (resolved at run time).
 */

export const USER_TYPE_ORDER: UserType[] = [
    UserType.SUPER_ADMIN,
    UserType.GROUP_MANAGER,
    UserType.HOTEL_MANAGER,
    UserType.GENERAL_MODULE_MANAGER,
    UserType.CRM_MODULE_MANAGER,
    UserType.MARKETING_MODULE_MANAGER,
    UserType.SPA_MODULE_MANAGER,
    UserType.RESTAURANT_MODULE_MANAGER,
    UserType.ACCOMMODATION_MODULE_MANAGER,
    UserType.RENTALS_MODULE_MANAGER,
    UserType.GENERAL_USER,
    UserType.CRM_USER,
    UserType.MARKETING_USER,
    UserType.SPA_USER,
    UserType.RESTAURANT_USER,
    UserType.ACCOMMODATION_USER,
    UserType.RENTALS_USER
];

const FIRST_BASIC_USER_TYPE = USER_TYPE_ORDER.indexOf(UserType.GENERAL_USER);

export const MANAGER_USER_TYPES: UserType[] = USER_TYPE_ORDER.slice(0, FIRST_BASIC_USER_TYPE);

/** The same key the users list translates. */
export const userTypeLabel = (userType: string, t: TFunction): string => t(`User Type ${userType}`);

export const userTypeGroupLabel = (userType: string, t: TFunction): string => (
    MANAGER_USER_TYPES.includes(userType as UserType) ? t('Managers') : t('Users')
);

export const APP_FILTER_KEYS: IdFilterKeys = {
    ids: 'appIds', names: 'appNames', id: 'appId', name: 'appName'
};

export const USER_FILTER_KEYS: IdFilterKeys = {
    ids: 'userIds', names: 'userNames', id: 'userId', name: 'userName'
};

export const EXCLUDED_USER_FILTER_KEYS: IdFilterKeys = {
    ids: 'excludedUserIds', names: 'excludedUserNames', id: 'excludedUserId', name: 'excludedUserName'
};

export const readUserTypes = (config?: Record<string, any> | null): string[] => (
    Array.isArray(config?.userTypes) ? config!.userTypes.filter((entry: unknown) => !!entry) : []
);

export const readAppFilter = (config?: Record<string, any> | null): StoredIdFilter =>
    readIdFilter(config, APP_FILTER_KEYS);

export const readUserFilter = (config?: Record<string, any> | null): StoredIdFilter =>
    readIdFilter(config, USER_FILTER_KEYS);

/** A step without a pick reaches nobody and cannot go live. */
export const hasTeamMemberPicks = (config?: Record<string, any> | null): boolean =>
    readUserTypes(config).length > 0 || readAppFilter(config).ids.length > 0 || readUserFilter(config).ids.length > 0;

export const writeAppFilter = (selection: {id: string; name?: string}[]): Record<string, any> =>
    writeIdFilter(selection, APP_FILTER_KEYS);

export const writeUserFilter = (selection: {id: string; name?: string}[]): Record<string, any> =>
    writeIdFilter(selection, USER_FILTER_KEYS);

export const readExcludedUserFilter = (config?: Record<string, any> | null): StoredIdFilter =>
    readIdFilter(config, EXCLUDED_USER_FILTER_KEYS);

export const writeExcludedUserFilter = (selection: {id: string; name?: string}[]): Record<string, any> =>
    writeIdFilter(selection, EXCLUDED_USER_FILTER_KEYS);

const userTypeLabels = (userTypes: string[], t: TFunction): string[] => {
    const known = USER_TYPE_ORDER.filter(entry => userTypes.includes(entry));
    const unknown = userTypes.filter(value => !USER_TYPE_ORDER.includes(value as UserType));
    return [...known, ...unknown].map(userType => userTypeLabel(userType, t));
};

/** Joined with "or" because any one pick is enough; empty when nobody is picked. */
export const formatTeamMemberFilters = (
    config: Record<string, any> | null | undefined,
    t: TFunction,
    language: string
): string => {
    const apps = readAppFilter(config);
    const users = readUserFilter(config);
    const excluded = readExcludedUserFilter(config);

    const parts = [
        ...userTypeLabels(readUserTypes(config), t),
        ...(apps.ids.length
            ? [apps.ids.length === 1 ? t('1 app') : t('{{count}} apps', {count: apps.ids.length})]
            : []),
        ...(users.ids.length
            ? [users.ids.length === 1 ? t('1 person') : t('{{count}} people', {count: users.ids.length})]
            : [])
    ];

    const picks = new Intl.ListFormat(language, {type: 'disjunction'}).format(parts);
    return picks && excluded.ids.length
        ? t('Team members card except', {picks, count: excluded.ids.length})
        : picks;
};
